import React, { useState, useEffect } from "react";
import Styles from "./Location.module.css";
import { Input, Button, Modal } from "@sainsburys-tech/fable";
import Headerfile from "../../common/Headerfile";
import Footerfile from "../../common/Footerfile";
import { alpha, styled } from "@mui/material/styles";
import { pink } from "@mui/material/colors";
import Switch from "@mui/material/Switch";
import { useGetPostsQuery, useNewPostMutation } from "../../../api/apiConfig";
import { useNavigate } from "react-router-dom";
import { getLogisticsRole } from "../../../utils/authToken";
import { USER_ROLE } from "../../../constants/roles";
import Unauthorized from "../../unauthorized/Unauthorized";

interface IState {
  Code: string;
  Name: string;
  Status: boolean;
}

interface IErrors {
  Code?: string;
  Name?: string;
}

interface IData {
  StatusCode: number;
  Status: string;
  TotalRecords?: number;
  Message?: string;
  Model?: any;
  Value?: string;
}

interface IResponse {
  data: IData;
  TotalRecords: number;
}

const initialState: IState = {
  Code: "",
  Name: "",
  Status: false
};
const Location = () => {
  const [values, setValues] = useState<IState>(initialState);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState<IErrors>({});
  const label = { inputProps: { "aria-label": "Color switch demo" } };
  const [apiHeading, setApiHeading] = useState<string | null>(null);
  const [apiMessage, setApiMessage] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const navigate = useNavigate();
  const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(false);

  const loggedUserRole = getLogisticsRole();

  const isDisabled =
    loggedUserRole?.includes(USER_ROLE.ADMIN) ||
    loggedUserRole?.includes(USER_ROLE.READ_WRITE)
      ? false
      : true;

  const [newLocation] = useNewPostMutation();

  const { isLoading, data, refetch } = useGetPostsQuery(
    `locations?pageNumber=${page}&pageSize=${rowsPerPage}`
  );

  useEffect(() => {
    refetch();
  }, []);

  const handleToggleChange = (name: string) => (event: any) => {
    handleChange(name, event.target.checked);
  };

  const handleChange = (name: string, value: string | boolean) => {
    setValues({ ...values, [name]: value });
  };

  const Android12Switch = styled(Switch)(({ theme }) => ({
    padding: 8,
    "& .MuiSwitch-switchBase.Mui-checked": {
      color: "#f06c00",
      "&:hover": {
        backgroundColor: alpha(pink[600], theme.palette.action.hoverOpacity)
      }
    },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
      backgroundColor: "#84776c"
    },
    "& .MuiSwitch-track": {
      borderRadius: 22 / 2,
      "&::before, &::after": {
        content: '""',
        position: "absolute",
        top: "50%",
        transform: "translateY(-50%)",
        width: 16,
        height: 16
      },
      "&::before": {
        backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
          theme.palette.getContrastText(theme.palette.primary.main)
        )}" d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"/></svg>')`,
        left: 12
      },
      "&::after": {
        backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
          theme.palette.getContrastText(theme.palette.primary.main)
        )}" d="M19,13H5V11H19V13Z" /></svg>')`,
        right: 12
      }
    },
    "& .MuiSwitch-thumb": {
      boxShadow: "none",
      width: 16,
      height: 16,
      margin: 2
    }
  }));

  const validate = (values: IState): IErrors => {
    let errors: IErrors = {};

    if (!values.Code) {
      errors.Code = "Location Id is required";
    }

    if (!values.Name) {
      errors.Name = "Location Name is required";
    }

    return errors;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitted(true);
    const validationErrors = validate(values);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length === 0) {
      setIsSaveButtonDisabled(true);
      try {
        const response = await newLocation({
          Method: "locations",
          Code: values.Code,
          Name: values.Name,
          Status: values.Status ? "Active" : "Inactive"
        });
        if (
          isIResponse(response) &&
          response.data.StatusCode === 0 &&
          response.data.Status === "Ok"
        ) {
          setIsSaveButtonDisabled(false);
          setApiMessage("Location created successfully" || null);
          setApiHeading("Success!!!");
          setIsOpen(true);
          setValues(initialState);
          refetch();
        } else if (isIResponse(response) && response.data) {
          setApiMessage(response.data.Message || null);
          setApiHeading("Error!!!");
          setIsOpen(true);
          setIsSaveButtonDisabled(false);
          console.error("Error response:", response);
        } else {
          console.error("Something Went Wrong..", response);
        }
      } catch (error) {
        console.error(error);
        setIsSaveButtonDisabled(false);
      }
    }
  };

  const handleNextPage = () => {
    if (data && page < Math.ceil(data.TotalRecords / rowsPerPage)) {
      setPage(page + 1);
    }
  };

  const handlePreviousPage = () => {
    if (data && page > 1) {
      setPage(page - 1);
    }
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setRowsPerPage(Number(event.target.value));
    setPage(1);
  };

  function isIResponse(response: any): response is IResponse {
    return response.data !== undefined && typeof response.data === "object";
  }

  return (
    <>
      <div className={Styles.mainContainer}>
        <Headerfile />
        {isDisabled ? (
          <Unauthorized />
        ) : (
          <>
            <form onSubmit={handleSubmit} className={Styles.formContainer}>
              <h5>Add Location</h5>
              <p className={Styles.rightAlign}>
                <span className={Styles.redAsterisk}>*</span>Indicates required
                field
              </p>{" "}
              <label className={Styles.label}>
                <Input
                  name="Id"
                  label="Location ID*"
                  autocomplete="off"
                  type="text"
                  onChange={(e) => handleChange("Code", e)}
                  value={values.Code}
                  className={Styles.input}
                />
                {isSubmitted && errors.Code && (
                  <div className={Styles.error}>{errors.Code}</div>
                )}
              </label>
              <label className={Styles.label}>
                <Input
                  name="Name"
                  label="Name*"
                  autocomplete="off"
                  type="text"
                  onChange={(e) => handleChange("Name", e)}
                  value={values.Name}
                  className={Styles.input}
                />
                {isSubmitted && errors.Name && (
                  <div className={Styles.error}>{errors.Name}</div>
                )}
              </label>
              <label className={Styles.label}>
                <b>Status</b>
                <Android12Switch
                  {...label}
                  checked={values.Status}
                  onChange={handleToggleChange("Status")}
                />
              </label>
              <Button
                type="submit"
                className={Styles.button}
                disabled={isSaveButtonDisabled}
              >
                Save
              </Button>
              <br />
            </form>
            <div className={Styles.tableContainer}>
              <h5 className={Styles.heading}>View Location</h5>
              {isLoading ? (
                <div className={Styles.loadingContainer}>
                  <div className={Styles.loadingSpinner}></div>
                  <div className={Styles.loadingText}>Loading...</div>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped table-borderless">
                    <thead>
                      <tr>
                        <th>Location ID</th>
                        <th>Location Name</th>
                        <th>Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data && data?.Model?.length > 0 ? (
                        data?.Model.map((location: any) => (
                          <tr key={location.LocationId}>
                            <td>{location.Code ? location.Code : "-"}</td>
                            <td>{location.Name}</td>
                            <td>{location.Status}</td>
                            <td>
                              <button
                                className={Styles.tableEditBtn}
                                onClick={() =>
                                  navigate(`/editlocation/${location.LocationId}`)
                                }
                              >
                                Edit
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr className={Styles.printerNotFound}>
                          <td colSpan={14} className="text-center">
                            No Locations Found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  <div className={Styles.pagination}>
                    <button
                      onClick={handlePreviousPage}
                      disabled={page === 1}
                      className={Styles.prevBtn}
                    >
                      Previous
                    </button>
                    <span>
                      Page {page} of{" "}
                      {data ? Math.ceil(data.TotalRecords / rowsPerPage) : 0}
                    </span>
                    <button
                      onClick={handleNextPage}
                      disabled={
                        !data ||
                        page === Math.ceil(data.TotalRecords / rowsPerPage)
                      }
                      className={Styles.nextBtn}
                    >
                      Next
                    </button>
                  </div>
                  <div className={Styles.rowsPerPage}>
                    <label htmlFor="rowsPerPage">Rows per page:</label>
                    <select
                      id="rowsPerPage"
                      value={rowsPerPage}
                      onChange={handleRowsPerPageChange}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={30}>30</option>
                      <option value={40}>40</option>
                      <option value={50}>50</option>
                    </select>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
        <Modal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          layout="default"
          header={{
            isDismissible: true
          }}
        >
          <div className="text-center fw-400">
            <h3 className="text-center fw-500">{apiHeading}</h3>
            <h5 className="fw-400">{apiMessage}</h5>
          </div>
        </Modal>
      </div>
      <Footerfile />
    </>
  );
};

export default Location;
